import React, { useState } from "react";
import { api } from "../lib/api";
import { useRole } from "../context/RoleContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import FrsCodeAssignPanel from "./FrsCodeAssignPanel";

/**
 * Multi-sheet FRS upload flow.
 *
 * Step 1: user picks a workbook → POST /projects/{id}/frs/upload-multi-staging
 * Backend parses every sheet, identifies scope per sheet name, flags N/A
 * sheets, and returns a per-sheet summary.
 *
 * Step 2: dialog shows the list. Active scopes are pre-selected; N/A scopes
 * are shown disabled with a grey treatment. Foreman can deselect any active
 * scope they don't want imported (e.g. waiting on revision).
 *
 * Step 3: confirm → POST /frs-staging/{staging_id}/apply-multi with the
 * include list. Each selected scope creates/updates its FRS items the same
 * way the single-sheet flow does.
 */
export default function FrsMultiUploadDialog({ projectId, open, onOpenChange, onApplied, masterWarning = false }) {
  const [staging, setStaging] = useState(null);    // backend staging response
  const [included, setIncluded] = useState({});     // {scope_code: bool}
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [mode, setMode] = useState("merge");         // merge | replace | clear_replace
  const [preflight, setPreflight] = useState(null);  // Cluster 14 gate + review
  const [ackArchive, setAckArchive] = useState(false);
  const [manualMap, setManualMap] = useState({});    // old room key → new room key
  const { role } = useRole();
  const isAdmin = String(role || "").toLowerCase().match(/^(admin|management)$/) !== null;

  const reset = () => {
    setStaging(null); setIncluded({}); setMode("merge");
    setPreflight(null); setAckArchive(false); setManualMap({});
  };

  const runPreflight = async (stagingId) => {
    try {
      const r = await api.get(`/projects/${projectId}/frs-replace/preflight`,
        { params: { staging_id: stagingId, role: role || "" } });
      setPreflight(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check the job for recorded work");
    }
  };

  const clearAndReplace = async () => {
    const codes = Object.entries(included).filter(([, on]) => on).map(([c]) => c);
    setApplying(true);
    try {
      const r = await api.post(`/projects/${projectId}/frs-replace/apply`, {
        staging_id: staging.staging_id, role: role || "",
        actor: localStorage.getItem("fieldops-installer-name") || role || "",
        include_scope_codes: codes, manual_map: manualMap, acknowledge_archive: ackArchive,
      });
      const d = r.data;
      toast.success(`FRS replaced — ${d.rows_loaded} rows loaded, ${d.rows_removed} removed, `
        + `${d.rooms_carried} rooms carried over${d.rooms_archived?.length ? `, ${d.rooms_archived.length} archived` : ""}`);
      onApplied?.();
      onOpenChange(false);
      reset();
    } catch (e) {
      const det = e.response?.data?.detail;
      if (det && det.error === "unmapped_progress") {
        toast.error(`${det.rooms.length} room(s) with progress are not in the new file — map them or tick the archive box`);
      } else {
        toast.error(typeof det === "string" ? det : "Clear & Replace failed");
      }
    } finally { setApplying(false); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/projects/${projectId}/frs/upload-multi-staging`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStaging(r.data);
      // Pre-select every non-N/A scope
      const init = {};
      for (const s of r.data.sheets || []) {
        init[s.scope_code] = !s.n_a;
      }
      setIncluded(init);
    } catch (err) {
      toast.error("Multi-FRS upload failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const apply = async () => {
    if (!staging) return;
    if (mode === "clear_replace") return clearAndReplace();
    const codes = Object.entries(included).filter(([, on]) => on).map(([c]) => c);
    if (codes.length === 0) { toast.error("Pick at least one scope"); return; }
    if (mode === "replace") {
      const ok = window.confirm(
        `REPLACE mode:\n\nAll existing rows for ${codes.length} selected scope${codes.length === 1 ? "" : "s"} that are NOT locked by an active mobilization will be permanently deleted before the new FRS is applied.\n\nLocked (mobilized) rows are preserved — quantity changes to those still generate change-order lines.\n\nContinue?`,
      );
      if (!ok) return;
    }
    setApplying(true);
    try {
      const r = await api.post(`/frs-staging/${staging.staging_id}/apply-multi`, {
        include_scope_codes: codes,
        mode,
      });
      const t = r.data.totals || {};
      const parts = [`${t.added || 0} added`, `${t.updated || 0} updated`];
      if (t.deltas) parts.push(`${t.deltas} change-order${t.deltas === 1 ? "" : "s"}`);
      if (mode === "replace" && t.removed) parts.push(`${t.removed} removed`);
      toast.success(`Imported ${codes.length} scope${codes.length === 1 ? "" : "s"} · ${parts.join(" · ")}`);
      try {
        const rc = await api.post(`/projects/${projectId}/frs/restamp-codes`);
        if (rc.data?.matched != null) {
          toast.success(`${rc.data.matched} of ${rc.data.total} rows carry a short FRS code`);
        }
      } catch { /* codes are cosmetic — never fail an import over them */ }
      onApplied?.();
      onOpenChange(false);
      reset();
    } catch {
      toast.error("Apply failed");
    } finally {
      setApplying(false);
    }
  };

  const closeAndReset = (next) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const sheets = staging?.sheets || [];
  const activeCount = sheets.filter((s) => !s.n_a).length;
  const naCount = sheets.length - activeCount;
  const selectedCount = Object.values(included).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={closeAndReset}>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="multi-frs-dialog"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="sticky top-0 z-10 bg-white dark:bg-gray-900 pb-2 -mx-6 px-6 border-b border-gray-100 dark:border-gray-800">
          <DialogTitle>Multi-FRS Upload</DialogTitle>
        </DialogHeader>

        {masterWarning && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200" data-testid="multi-frs-master-warning">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-semibold">You are about to override the master FRS.</div>
              This is the one FRS shared by Jobs and RA. <strong>Replace</strong> swaps the full FRS for the uploaded workbook;
              <strong> Merge</strong> adds and updates rows and logs every change (quantity changes on mobilized rows become change-order lines).
            </div>
          </div>
        )}

        {!staging && (
          <div className="py-6 flex flex-col items-center gap-3 text-sm">
            <div className="text-gray-700 text-center max-w-md">
              Upload a workbook with one tab per scope. The system reads each
              sheet, matches it to a scope code (BIKE / CCT / FPS / Mirrors /
              FRP / WP / Lockers / Postal / TC / VDU…), and flags scopes that
              are empty / &quot;Leave This Row Open&quot;.
            </div>
            <label className="cursor-pointer">
              <input
                data-testid="multi-frs-upload-input"
                type="file"
                accept=".xlsx,.xlsm,.xls"
                className="hidden"
                onChange={onPick}
              />
              <span className="inline-flex items-center gap-1 h-10 px-4 text-sm font-medium border border-blue-300 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100">
                {uploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Parsing…</>) : "Choose Workbook…"}
              </span>
            </label>
          </div>
        )}

        {staging && (
          <div className="space-y-3">
            <div className="text-xs text-gray-500 flex items-center justify-between">
              <span><strong>{staging.file_name}</strong></span>
              <span>
                {activeCount} active · {naCount} N/A · {selectedCount} selected
              </span>
            </div>
            <FrsCodeAssignPanel report={staging.item_codes} />

            {/* Merge vs Replace picker — placed BEFORE the sheet list so it's
                impossible to miss. Wraps to a horizontal segmented control on
                wider screens so it never hides under a scrollbar. */}
            <div className="border-2 border-gray-300 dark:border-gray-700 rounded-md p-3 bg-gray-50 dark:bg-gray-900/60 space-y-2">
              <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wide flex items-center gap-2">
                <span>How to apply this FRS</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                  mode === "clear_replace"
                    ? "bg-red-600 text-white"
                    : mode === "replace"
                      ? "bg-rose-600 text-white"
                      : "bg-blue-600 text-white"
                }`}>{mode === "clear_replace" ? "CLEAR & REPLACE" : mode.toUpperCase()}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label
                  className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors ${
                    mode === "merge" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                  data-testid="multi-frs-mode-merge"
                >
                  <input
                    type="radio"
                    name="frs-apply-mode"
                    value="merge"
                    checked={mode === "merge"}
                    onChange={() => setMode("merge")}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-semibold text-gray-900">Merge <span className="text-[10px] font-normal text-gray-500">(default)</span></div>
                    <div className="text-gray-600 mt-0.5">
                      Adds new rows, updates existing ones. Rows missing from the new FRS stay in place as &quot;cancelled&quot;. Quantity changes on already-mobilized rows create change-order lines instead of overwriting.
                    </div>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors ${
                    mode === "replace" ? "border-rose-500 bg-rose-50 ring-2 ring-rose-200" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                  data-testid="multi-frs-mode-replace"
                >
                  <input
                    type="radio"
                    name="frs-apply-mode"
                    value="replace"
                    checked={mode === "replace"}
                    onChange={() => setMode("replace")}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-semibold text-gray-900">Replace</div>
                    <div className="text-gray-600 mt-0.5">
                      Hard-deletes any row (in ticked scopes) that isn&apos;t in the new FRS. Locked rows&apos; history is preserved on the mobilization. Use after a rename to fully clean stale hierarchy values.
                    </div>
                  </div>
                </label>
                <label
                  className={`flex items-start gap-2 rounded-md border p-2 cursor-pointer transition-colors sm:col-span-2 ${
                    mode === "clear_replace" ? "border-red-600 bg-red-50 ring-2 ring-red-200" : "border-gray-200 bg-white hover:bg-gray-50"
                  }`}
                  data-testid="multi-frs-mode-clear-replace"
                >
                  <input
                    type="radio"
                    name="frs-apply-mode"
                    value="clear_replace"
                    checked={mode === "clear_replace"}
                    onChange={() => { setMode("clear_replace"); runPreflight(staging.staging_id); }}
                    className="mt-0.5"
                  />
                  <div className="text-xs">
                    <div className="font-semibold text-red-800">Clear &amp; Replace FRS</div>
                    <div className="text-gray-600 mt-0.5">
                      Deletes the whole FRS structure (buildings, floors, areas, rooms, product
                      assignments) and loads this file fresh. Blocked when the job has recorded
                      work unless an administrator carries it over. Always snapshots first, so it can
                      be restored.
                    </div>
                  </div>
                </label>
              </div>
            </div>
            {mode === "clear_replace" && preflight && (
              <div className="border-2 border-red-300 rounded-md p-3 bg-red-50/60 space-y-3" data-testid="clear-replace-panel">
                <div className="text-xs font-semibold uppercase tracking-wide text-red-800">Recorded work on this job</div>
                <div className="flex flex-wrap gap-2 text-xs" data-testid="clear-replace-blockers">
                  {Object.entries(preflight.blockers || {}).map(([k, v]) => (
                    <span key={k} data-testid={`clear-replace-blocker-${k}`}
                          className={`px-2 py-0.5 rounded border font-medium ${v > 0 ? "bg-white border-red-300 text-red-800" : "bg-white border-gray-200 text-gray-400"}`}>
                      {k.replace(/_/g, " ")}: <strong>{v}</strong>
                    </span>
                  ))}
                </div>
                {preflight.blocked && !isAdmin && (
                  <div className="text-xs text-red-800 font-semibold" data-testid="clear-replace-blocked-msg">
                    Blocked — this job has recorded work. An administrator can proceed through the
                    preservation flow below.
                  </div>
                )}
                {preflight.review && (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      {[["matched", "Matched rooms"], ["new_rooms", "New rooms"], ["unmatched_old_with_progress", "Unmatched with progress"]].map(([k, label]) => (
                        <div key={k} className="bg-white border border-gray-200 rounded p-2" data-testid={`clear-replace-count-${k}`}>
                          <div className="text-lg font-bold text-gray-900">{(preflight.review[k] || []).length}</div>
                          <div className="text-[10px] uppercase text-gray-500">{label}</div>
                        </div>
                      ))}
                    </div>
                    {(preflight.review.unmatched_old_with_progress || []).length > 0 && (
                      <div className="bg-white border border-amber-300 rounded p-2 space-y-2" data-testid="clear-replace-unmatched">
                        <div className="text-xs font-semibold text-amber-800">
                          These rooms hold progress and are not in the new file — map each one to a new room, or acknowledge the archive.
                        </div>
                        <table className="w-full text-[11px]">
                          <thead className="text-gray-500 uppercase text-[10px]">
                            <tr><th className="text-left py-1">Old room</th><th className="text-left py-1">Progress</th><th className="text-left py-1">Map to</th></tr>
                          </thead>
                          <tbody>
                            {(preflight.review.unmatched_old_with_progress || []).map((r) => (
                              <tr key={r.key} className="border-t border-gray-100">
                                <td className="py-1">{[r.building, r.floor && `Fl ${r.floor}`, r.room_number, r.room_name].filter(Boolean).join(" · ")}</td>
                                <td className="py-1 text-gray-600">
                                  {r.progress.percent}% · {r.progress.photos} photo(s){r.progress.punch_items ? ` · ${r.progress.punch_items} punch` : ""}
                                </td>
                                <td className="py-1">
                                  <select
                                    value={manualMap[r.key] || ""}
                                    onChange={(e) => setManualMap({ ...manualMap, [r.key]: e.target.value })}
                                    data-testid={`clear-replace-map-${r.key}`}
                                    className="h-7 text-[11px] border border-gray-300 rounded px-1 bg-white"
                                  >
                                    <option value="">— archive it —</option>
                                    {[...(preflight.review.matched || []), ...(preflight.review.new_rooms || [])].map((n) => (
                                      <option key={n.key} value={n.key}>
                                        {[n.building, n.floor && `Fl ${n.floor}`, n.room_number].filter(Boolean).join(" · ")}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <label className="flex items-start gap-2 text-[11px] text-gray-700">
                          <input type="checkbox" checked={ackArchive} onChange={(e) => setAckArchive(e.target.checked)}
                                 data-testid="clear-replace-ack-archive" className="mt-0.5" />
                          I understand the progress on any room left unmapped will be <strong>archived</strong> (kept and viewable on the project), not deleted.
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium w-10"></th>
                    <th className="text-left px-3 py-2 font-medium">Sheet</th>
                    <th className="text-left px-3 py-2 font-medium w-20">Scope</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Rooms</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Units</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Added</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Updated</th>
                    <th className="text-right px-3 py-2 font-medium w-16">Removed</th>
                    <th className="text-left px-3 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((s) => {
                    const checked = !!included[s.scope_code];
                    return (
                      <tr key={s.scope_code + s.sheet_name} className={`border-t border-gray-100 ${s.n_a ? "bg-gray-50/60 text-gray-400" : "hover:bg-blue-50/30"}`}>
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            disabled={s.n_a}
                            checked={checked}
                            onChange={() => setIncluded({ ...included, [s.scope_code]: !checked })}
                            data-testid={`multi-frs-include-${s.scope_code}`}
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{s.sheet_name}</td>
                        <td className="px-3 py-2 font-mono text-[11px] text-blue-700">{s.scope_code}</td>
                        <td className="px-3 py-2 text-right">{s.row_count}</td>
                        <td className="px-3 py-2 text-right">{s.total_units}</td>
                        <td className="px-3 py-2 text-right text-emerald-700 font-medium">{s.summary?.added || 0}</td>
                        <td className="px-3 py-2 text-right text-amber-700 font-medium">{s.summary?.updated || 0}</td>
                        <td
                          className={`px-3 py-2 text-right font-medium ${
                            (s.summary?.removed || 0) === 0
                              ? "text-gray-400"
                              : mode === "replace"
                                ? "text-rose-700"
                                : "text-gray-500"
                          }`}
                          title={
                            (s.summary?.removed || 0) === 0
                              ? "No rows to remove"
                              : mode === "replace"
                                ? `${s.summary?.removed} row${s.summary?.removed === 1 ? "" : "s"} will be permanently deleted`
                                : `${s.summary?.removed} row${s.summary?.removed === 1 ? "" : "s"} will be marked cancelled (Merge mode)`
                          }
                        >
                          {s.summary?.removed || 0}
                        </td>
                        <td className="px-3 py-2">
                          {s.n_a ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-gray-500">
                              <XCircle className="w-3.5 h-3.5" /> N/A — {s.reason || "no quantities"}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Active · {s.product_cols?.length || 0} product col{s.product_cols?.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {sheets.length === 0 && (
                    <tr><td colSpan={9} className="px-3 py-8 text-center text-gray-500">No scope sheets found in this workbook.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {naCount > 0 && (
              <div className="text-[11px] text-gray-500 inline-flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
                N/A sheets are skipped (empty quantities or &quot;Leave This Row Open&quot; only).
              </div>
            )}
          </div>
        )}

        <DialogFooter className="sticky bottom-0 z-10 bg-white dark:bg-gray-900 pt-3 -mx-6 px-6 border-t border-gray-100 dark:border-gray-800">
          <Button variant="outline" onClick={() => closeAndReset(false)} disabled={applying}>Cancel</Button>
          {staging && (
            <Button
              onClick={apply}
              disabled={applying || selectedCount === 0
                || (mode === "clear_replace" && preflight?.blocked && !isAdmin)}
              className={mode === "clear_replace"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : mode === "replace"
                  ? "bg-rose-600 hover:bg-rose-700 text-white"
                  : "bg-blue-600 hover:bg-blue-700 text-white"}
              data-testid="multi-frs-apply-btn"
            >
              {applying
                ? (<><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying…</>)
                : mode === "clear_replace"
                  ? "Clear & Replace FRS"
                  : `${mode === "replace" ? "Replace" : "Merge"} · ${selectedCount} scope${selectedCount === 1 ? "" : "s"}`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
